import { errorMessageStore } from '../stores/ErrorMessageStore';

import { InvalidLinkError } from './ErrorHandler';
import { trackViewerInteraction } from './telemetry/matomo';

import { getEnvVar } from '@egr/xbox/utils/ReactScriptHelper';
import { android } from '@egr/xbox/utils/UserAgent';
import { captureException } from '@egr/xbox/utils/errors/Error';
import { Logger } from '@egr/xbox/utils/errors/Logger';

import { joinPath } from '@easterngraphics/wcf/modules/utils/string';

const API_URL = joinPath(getEnvVar('BACKEND_API_BASE_URL') ?? '', '/v1/dlinfo');

const getDeviceType = (): 'android' | 'ios' => {
    return android ? 'android' : 'ios';
};

interface APIRequest {
    shareURL: string;
    deviceType?: string;
}

interface APIResponse {
    fileExtension: string;
    downloadURL: string;
    showAR?: boolean;
    validUntil?: string;
}

const fetchAPI = async (request: APIRequest): Promise<APIResponse> => {
    const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        const json = await response.json();
        if (json.error === 'Error retrieving file name from 0 byte get: Request failed with status code 404') {
            throw new InvalidLinkError();
        } else if (json.error === 'Provided share URL is not a valid URL') {
            throw new InvalidLinkError();
        }
        throw new Error('Failed to fetch API response');
    }

    return response.json();
};

function getValidDate(valid: string | undefined): number | undefined {
    return valid && valid?.length === 8 ? Date.parse(valid.slice(0, 4) + '-' + valid.slice(4, 6) + '-' + valid.slice(6, 8)) : undefined;
}

export const getQueryParam = (param: string): string | undefined => {
    const params = new URLSearchParams(window.location.search);
    const value = params.get(param);
    return value ? decodeURIComponent(value) : undefined;
};

interface UrlExtraction {
    url: string;
    embed: boolean;
    showAR: boolean;
    validUntil?: number;
}
export const extractUrlParameters = (): UrlExtraction | undefined => {
    const urlParam = getQueryParam('url') ?? getQueryParam('fileUrl');
    const embedParam = getQueryParam('embed');
    const editParam = getQueryParam('edit');
    const viewParam = getQueryParam('view');

    const glbParam = getQueryParam('glb');
    const usdzParam = getQueryParam('usdz');

    const validUntil = getQueryParam('valid');

    if (urlParam ?? embedParam ?? editParam ?? viewParam) {
        return {
            url: urlParam ?? embedParam ?? editParam ?? viewParam!,
            embed: embedParam !== undefined,
            showAR: false,
            validUntil: getValidDate(validUntil),
        };
    }

    if (glbParam && usdzParam) {
        return {
            url: atob(getDeviceType() === 'android' ? glbParam : usdzParam),
            embed: false,
            showAR: true,
            validUntil: getValidDate(validUntil),
        };
    }

    return undefined;
};

export type FileExtension = '' | 'jpg' | 'jpeg' | 'png' | 'epan' | 'eimg' | 'pec' | 'glb' | 'gltf' | 'usdz' | 'iar' | 'pbox';
export interface FileUrlInfo {
    fileUrl: string; // This is the share link provided by the hoster
    fileDownloadUrl: string; // This is the direct download link for the file
    extension: FileExtension;
    arFile: boolean;
    validUntil?: number;
}
export async function fetchUrlInfo(shareUrl?: string): Promise<FileUrlInfo | undefined> {
    const urlExtraction = extractUrlParameters();
    const url = shareUrl ?? urlExtraction?.url;

    if (!url) {
        return undefined;
    }

    const deviceType = getDeviceType();

    try {
        const apiResponse = await fetchAPI({ shareURL: url, deviceType });

        return {
            fileUrl: url,
            fileDownloadUrl: apiResponse.downloadURL,
            extension: apiResponse.fileExtension as FileExtension,
            arFile: apiResponse.showAR ?? urlExtraction?.showAR ?? false,
            validUntil: apiResponse.validUntil ? Date.parse(apiResponse.validUntil) : urlExtraction?.validUntil,
        };
    } catch (error) {
        captureException(error, 'debug');
        Logger.warn('URL-API request failed, trying fallback method.');
        Logger.warn(error);
        errorMessageStore.open(error);
        return;
    }
}

export function openURLAsStandAlone(uri: string) {
    const url = new URL(uri);
    const params = url.searchParams;
    const embedUrl = params.get('embed');
    if (embedUrl != null) {
        params.delete('embed');
        params.set('url', embedUrl);
    }
    trackViewerInteraction('Open standalone');
    window.open(url.toString(), '_blank');
}
